/**
 * 我的钱包页面 - 运营商资金流水
 */

import { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { supabase } from '@/lib/supabase'

interface FundFlow {
  id: string
  amount: number
  balance_type: number
  flow_type: number
  biz_type: number
  remark: string
  created_at: string
}

export default function MyWalletPage() {
  const { user } = useAuthStore()
  const [fundFlows, setFundFlows] = useState<FundFlow[]>([])
  const [loading, setLoading] = useState(true)
  
  // 运营商账户信息
  const [operatorAccount, setOperatorAccount] = useState({
    balance: 0,
    commission_balance: 0
  })

  useEffect(() => {
    fetchOperatorAccount()
    fetchFundFlows()
  }, [])

  const fetchOperatorAccount = async () => {
    try {
      const { data: adminUser, error: adminError } = await supabase
        .from('admin_users')
        .select('operator_id, role')
        .eq('id', user?.id)
        .maybeSingle()

      if (adminError || !adminUser || adminUser.role !== 'operator' || !adminUser.operator_id) {
        setOperatorAccount({ balance: 0, commission_balance: 0 })
        return
      }

      const { data, error } = await supabase
        .from('operator_accounts')
        .select('balance, commission_balance')
        .eq('operator_id', adminUser.operator_id)
        .maybeSingle()

      if (data) {
        setOperatorAccount({
          balance: parseFloat(data.balance || '0'),
          commission_balance: parseFloat(data.commission_balance || '0')
        })
      }
    } catch (error) {
      console.error('Failed to fetch operator account:', error)
    }
  }

  const fetchFundFlows = async () => {
    try {
      setLoading(true)
      
      const { data: adminUser, error: adminError } = await supabase
        .from('admin_users')
        .select('operator_id')
        .eq('id', user?.id)
        .maybeSingle()

      if (adminError || !adminUser?.operator_id) {
        setFundFlows([])
        return
      }

      const { data, error } = await supabase
        .from('operator_fund_flows')
        .select('id, amount, balance_type, flow_type, biz_type, remark, created_at')
        .eq('operator_id', adminUser.operator_id)
        .order('created_at', { ascending: false })
        .limit(100)

      if (error) throw error

      const formattedData = (data || []).map((item: any) => ({
        id: item.id,
        amount: parseFloat(item.amount),
        balance_type: item.balance_type,
        flow_type: item.flow_type,
        biz_type: item.biz_type,
        remark: item.remark,
        created_at: item.created_at
      }))

      setFundFlows(formattedData)
    } catch (error) {
      console.error('Failed to fetch fund flows:', error)
    } finally {
      setLoading(false)
    }
  }

  const getBalanceTypeLabel = (type: number) => {
    return type === 1 ? '账户余额' : '佣金余额'
  }

  const getFlowTypeLabel = (type: number) => {
    return type === 1 ? '收入' : '支出'
  }

  const getBizTypeLabel = (type: number) => {
    const labels: Record<number, string> = {
      401: '充值',
      402: '提现',
      501: '创建用户',
      502: '返还佣金',
      601: '佣金收入'
    }
    return labels[type] || '其他'
  }

  const getFlowTypeColor = (type: number) => {
    return type === 1 ? 'text-green-600' : 'text-red-600'
  }

  return (
    <div className="page-content">
      <div className="mb-8">
        <h1 className="page-title">我的钱包</h1>
        <p className="page-subtitle">查看账户余额和资金流水</p>
      </div>

      {/* 账户余额卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <div className="card-body">
            <h3 className="text-sm font-medium text-gray-500 mb-2">账户余额</h3>
            <p className="text-3xl font-bold text-blue-600">
              ¥{operatorAccount.balance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-gray-500 mt-2">用于创建用户和充值赠金</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h3 className="text-sm font-medium text-gray-500 mb-2">佣金余额</h3>
            <p className="text-3xl font-bold text-green-600">
              ¥{operatorAccount.commission_balance.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-gray-500 mt-2">来自用户交易手续费分成</p>
          </div>
        </div>
      </div>

      {/* 资金流水 */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">资金流水</h3>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-12">
              <div className="text-gray-500">加载中...</div>
            </div>
          ) : fundFlows.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">还没有资金流水记录</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      业务类型
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      余额类型
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      收支类型
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      金额
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      备注
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {fundFlows.map((flow) => (
                    <tr key={flow.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(flow.created_at).toLocaleString('zh-CN')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {getBizTypeLabel(flow.biz_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {getBalanceTypeLabel(flow.balance_type)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {getFlowTypeLabel(flow.flow_type)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className={`text-sm font-medium ${getFlowTypeColor(flow.flow_type)}`}>
                          {flow.flow_type === 1 ? '+' : '-'}¥{flow.amount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500 max-w-xs truncate">
                          {flow.remark || '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
